import axios from 'axios';
import { executeRecaptcha } from './recaptcha';

/*
|------------------------------------------------------------------------------
| NACIS API client
|------------------------------------------------------------------------------
| One axios instance shared across the app, with:
|   - baseURL from VITE_API_URL
|   - bearer token from localStorage on every request
|   - 401 handler that clears token + redirects to /login
|   - subsystem-grouped helpers below (rerrApi, ifpsApi, firlApi, verisApi, hubApi…)
*/

export const TOKEN_KEY = 'nacis.token';
export const USER_KEY  = 'nacis.user';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
  headers: { Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Device fingerprint header — the `device.attested` middleware matches this
  // against a Device row, so it must be present on every request, not just
  // RERR submissions. See ensureDeviceFingerprint() / deviceApi.attest below.
  const fp = localStorage.getItem('nacis.device_fingerprint');
  if (fp) config.headers['X-Device-Fingerprint'] = fp;
  return config;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // Hard redirect — clears in-memory state including QueryClient cache.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login?reason=session_expired');
      }
    }
    return Promise.reject(err);
  },
);

const unwrap = p => p.then(r => r.data);

// ── Auth ─────────────────────────────────────────────────────────────────────
//
// Login / register / OTP verification all return the same shape
// when successful: { token, user, roles, permissions }. The OTP-issuing
// endpoints (register, login-when-unverified, resend-otp) ALSO return
// `dev_otp` in non-prod/non-staging environments so dev can read the
// code straight from the response without an SMTP round-trip.
// login / register / forgot-password auto-attach a v3 reCAPTCHA token;
// backend's enforceRecaptcha picks it up. Returns null when not configured.
const withRecaptcha = async (action, data) => {
  const recaptcha_token = await executeRecaptcha(action);
  return { ...data, recaptcha_token };
};

// Declares which client is asking for a token. The backend gives a `web`
// token a short lifetime (hours) because a browser holds it in localStorage
// where any XSS can read it; a handset keystore is a different risk and
// keeps the long-lived variant. Omitting this silently gets the 30-day
// token, so every token-issuing call below must send it.
const WEB_CLIENT = { client: 'web' };

export const authApi = {
  login:        async (email, password)         => unwrap(api.post('/auth/login',    { ...await withRecaptcha('login', { email, password }), ...WEB_CLIENT })),
  logout:       ()                              => unwrap(api.post('/auth/logout')),
  me:           ()                              => unwrap(api.get('/auth/me')),

  // Self-signup + email-OTP verification
  register:     async (payload)                 => unwrap(api.post('/auth/register', await withRecaptcha('register', payload))),
  verifyOtp:    (email, code)                   => unwrap(api.post('/auth/verify-otp',     { email, code, ...WEB_CLIENT })),
  resendOtp:    (email)                         => unwrap(api.post('/auth/resend-otp',     { email })),

  // ── Account self-service ───────────────────────────────────────────────────
  updateProfile:   payload   => unwrap(api.put('/auth/profile', payload)),
  // `mode` is anonymous | identified — it governs whether a report carries
  // attributable submitter identity, not merely a display preference.
  updatePrivacyMode: mode    => unwrap(api.put('/auth/privacy-mode', { mode })),
  permissions:     ()        => unwrap(api.get('/auth/permissions')),
  logoutAll:       ()        => unwrap(api.post('/auth/logout/all')),
  // Rotates the caller's token: mints a new one and destroys the current.
  // Sends the web hint too, or a rotation would silently upgrade the
  // session back to the 30-day mobile lifetime.
  refresh:         ()        => unwrap(api.post('/auth/refresh', WEB_CLIENT)),

  // Password reset (the BE already exposed these; surface them here too)
  forgotPassword: async (identifier)            => unwrap(api.post('/auth/forgot-password', await withRecaptcha('forgot_password', { identifier }))),
  resetPassword:  (identifier, code, newPassword) =>
                                                  unwrap(api.post('/auth/reset-password',  {
                                                    identifier,
                                                    code,
                                                    new_password: newPassword,
                                                  })),
};

// ── Device Trust Authority ───────────────────────────────────────────────────
//
// Every `/rerr/citizen/*` and `/vision/citizen/*` route sits behind the
// `device.attested` middleware, which requires a Device row matching the
// X-Device-Fingerprint header this client sends on every request. Registering
// the browser as a `web`-platform device is what makes those routes reachable;
// without it they answer 403 DEVICE_UNKNOWN.
export const DEVICE_FP_KEY = 'nacis.device_fingerprint';

export function ensureDeviceFingerprint() {
  let fp = localStorage.getItem(DEVICE_FP_KEY);
  if (!fp) {
    const rand = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    fp = `web-${rand}`;
    localStorage.setItem(DEVICE_FP_KEY, fp);
  }
  return fp;
}

export const deviceApi = {
  // Browsers carry no hardware attestation, so we declare `unattested`
  // honestly and let the Device Trust Authority score it accordingly —
  // a web session should not claim the trust level of an attested handset.
  attest: (extra = {}) => unwrap(api.post('/auth/device/attest', {
    device_fingerprint: ensureDeviceFingerprint(),
    platform:           'web',
    device_model:       'browser',
    os:                 navigator.platform || 'unknown',
    app_version:        'web',
    attestation:        { kind: 'unattested' },
    ...extra,
  })),
  list:   ()   => unwrap(api.get('/auth/devices')),
  revoke: id   => unwrap(api.delete(`/auth/devices/${id}`)),
};

// ── RERR ─────────────────────────────────────────────────────────────────────
export const rerrApi = {
  // Citizen
  submitReport:     payload          => unwrap(api.post('/rerr/citizen/reports', payload)),
  myReports:        params           => unwrap(api.get('/rerr/citizen/reports',         { params })),
  getReport:        id               => unwrap(api.get(`/rerr/citizen/reports/${id}`)),

  // Agency / dispatch
  getDispatch:      params           => unwrap(api.get('/rerr/agency/dispatch',         { params })),
  acceptTask:       taskId           => unwrap(api.post(`/rerr/agency/dispatch/${taskId}/accept`)),
  resolveTask:      (taskId, body)   => unwrap(api.post(`/rerr/agency/dispatch/${taskId}/resolve`, body || {})),
  updateTaskStatus: (taskId, status) => unwrap(api.post(`/rerr/agency/dispatch/${taskId}/update`, { status })),

  // Agency BOLO (publish + lifecycle)
  // The lifecycle action is canonically called "resolve" (status → resolved/apprehended/expired)
  // per the BoloController, not "revoke". Helper alias kept for back-compat.
  listBolos:        params           => unwrap(api.get('/rerr/agency/bolo',             { params })),
  publishBolo:      payload          => unwrap(api.post('/rerr/agency/bolo', payload)),
  updateBolo:       (id, body)       => unwrap(api.put(`/rerr/agency/bolo/${id}`, body)),
  resolveBolo:      (id, outcome = 'resolved') =>
                                          unwrap(api.post(`/rerr/agency/bolo/${id}/resolve`, { outcome })),
  // Back-compat alias.
  revokeBolo:       (id)             => unwrap(api.post(`/rerr/agency/bolo/${id}/resolve`, { outcome: 'resolved' })),

  // CBIM — Citizen BOLO Interaction Module (P5)
  listCitizenBolos: params           => unwrap(api.get('/rerr/citizen/bolo',          { params })),
  showCitizenBolo:  id               => unwrap(api.get(`/rerr/citizen/bolo/${id}`)),
  issueBoloConsent: (id, body)       => unwrap(api.post(`/rerr/citizen/bolo/${id}/consent`, body)),
  respondToBolo:    (id, body)       => unwrap(api.post(`/rerr/citizen/bolo/${id}/respond`, body)),
  submitBoloReport: (id, body)       => unwrap(api.post(`/rerr/citizen/bolo/${id}/submit`, body)),
  myBoloConsents:   ()               => unwrap(api.get('/rerr/citizen/bolo/consent')),
  revokeBoloConsent:(id, reason)     => unwrap(api.post(`/rerr/citizen/bolo/consent/${id}/revoke`, { reason })),

  // CBIM — Citizen Alert Broadcast (outbound, P5-08)
  citizenBroadcastFeed:    ()             => unwrap(api.get('/cbim/citizen/broadcasts/feed')),
  myBroadcastOptin:        ()             => unwrap(api.get('/cbim/citizen/broadcasts/optin')),
  enrollBroadcastOptin:    body           => unwrap(api.post('/cbim/citizen/broadcasts/optin', body)),
  withdrawBroadcastOptin:  body           => unwrap(api.post('/cbim/citizen/broadcasts/optin/withdraw', body)),

  // Agency-side outbound broadcast issuance.
  agencyBroadcasts:        params         => unwrap(api.get('/cbim/agency/broadcasts',    { params })),
  issueBroadcast:          body           => unwrap(api.post('/cbim/agency/broadcasts', body)),
  revokeBroadcast:         (id, reason)   => unwrap(api.post(`/cbim/agency/broadcasts/${id}/revoke`, { reason })),

  // ── Agency — Routing manifests ──────────────────────────────────────────────
  listManifests:    params           => unwrap(api.get('/rerr/agency/manifests',       { params })),
  getManifest:      id               => unwrap(api.get(`/rerr/agency/manifests/${id}`)),
  ackManifest:      id               => unwrap(api.post(`/rerr/agency/manifests/${id}/ack`)),

  // ── Agency — Field units / AVL ──────────────────────────────────────────────
  listUnits:        params           => unwrap(api.get('/rerr/agency/units',           { params })),
  getUnit:          id               => unwrap(api.get(`/rerr/agency/units/${id}`)),
  updateUnitPosition: body           => unwrap(api.post('/rerr/agency/units/position', body)),

  // ── Agency — Evidence + chain of custody ────────────────────────────────────
  // These are the agency-scoped audit surfaces. `reportAudit` below points at
  // the hub-scoped full trace, which requires role:hub_admin.
  reportEvidence:   id               => unwrap(api.get(`/rerr/agency/reports/${id}/evidence`)),
  reportChain:      id               => unwrap(api.get(`/rerr/agency/reports/${id}/chain`)),

  // ── Hub — report intake oversight ───────────────────────────────────────────
  hubReports:       params           => unwrap(api.get('/rerr/hub/reports',            { params })),
  hubReport:        id               => unwrap(api.get(`/rerr/hub/reports/${id}`)),
  hubRoutingLog:    params           => unwrap(api.get('/rerr/hub/routing',            { params })),
  hubStats:         params           => unwrap(api.get('/rerr/hub/stats',              { params })),

  // ── Hub — agency registry ───────────────────────────────────────────────────
  listAgencies:     params           => unwrap(api.get('/rerr/hub/agencies',           { params })),
  createAgency:     body             => unwrap(api.post('/rerr/hub/agencies', body)),
  updateAgency:     (id, body)       => unwrap(api.put(`/rerr/hub/agencies/${id}`, body)),

  // ── Hub — RERR-scoped audit ─────────────────────────────────────────────────
  rerrAudit:        params           => unwrap(api.get('/rerr/hub/audit',              { params })),
  // Full cross-subsystem trace of one report (hub_admin only).
  reportAudit:      id               => unwrap(api.get(`/rerr/hub/audit/${id}`)),

  // ── Citizen — chunked media upload + status ────────────────────────────────
  initMediaUpload:  (reportId, body) => unwrap(api.post(`/rerr/citizen/reports/${reportId}/media`, body)),
  uploadMediaChunk: (reportId, mediaId, body, config) =>
                       unwrap(api.post(`/rerr/citizen/reports/${reportId}/media/${mediaId}/chunk`, body, config)),
  completeMediaUpload: (reportId, mediaId, body) =>
                       unwrap(api.post(`/rerr/citizen/reports/${reportId}/media/${mediaId}/complete`, body || {})),
  reportStatus:     reportId         => unwrap(api.get(`/rerr/citizen/reports/${reportId}/status`)),
};

// ── IFPS ─────────────────────────────────────────────────────────────────────
export const ifpsApi = {
  // Citizen side — Live Authorization Challenges
  myChallenges:     params               => unwrap(api.get('/ifps/citizen/challenges/pending', { params })),
  showChallenge:    id                   => unwrap(api.get(`/ifps/citizen/challenges/${id}`)),
  respondChallenge: (id, response, note) => unwrap(api.post(`/ifps/citizen/challenge/${id}/respond`, { response, note })),

  // Supervisor / partner side — challenges (added by Batch A2)
  listChallenges:   params               => unwrap(api.get('/ifps/admin/challenges',           { params })),
  getChallenge:     id                   => unwrap(api.get(`/ifps/admin/challenges/${id}`)),
  expireChallenge:  (id, reason)         => unwrap(api.post(`/ifps/admin/challenges/${id}/expire`, { reason })),

  // Identities (admin / supervisor)
  listIdentities:    params              => unwrap(api.get('/ifps/admin/identity-states',      { params })),

  // ── Transaction-boundary monitor (ifps_supervisor / hub_admin) ─────────────
  listTransactions:  params              => unwrap(api.get('/ifps/admin/transactions',         { params })),
  getTransaction:    id                  => unwrap(api.get(`/ifps/admin/transactions/${id}`)),

  // ── Institutional partner surface (role:institutional_partner) ─────────────
  // A bank / lender / registry invokes IFPS at a transaction boundary, then
  // polls status until the citizen answers the live authorization challenge.
  verifyTransaction:   body              => unwrap(api.post('/ifps/institution/transactions/verify', body)),
  transactionStatus:   txId              => unwrap(api.get(`/ifps/institution/transactions/${txId}/status`)),
  // Paginated ({ data, current_page, ... }). Pass status=open for everything
  // still awaiting an answer; an exact enum value filters precisely.
  institutionChallenges: params          => unwrap(api.get('/ifps/institution/challenges', { params })),
  institutionSignals:  params            => unwrap(api.get('/ifps/institution/signals',        { params })),
  ackInstitutionSignal: (id, body)       => unwrap(api.post(`/ifps/institution/signals/${id}/ack`, body || {})),
  identityState:     id                  => unwrap(api.get(`/ifps/admin/identity-states/${id}`)),
  identityHistory:   userId              => unwrap(api.get(`/ifps/admin/identities/${userId}/history`)),
  transitionIdentity:(id, body)          => unwrap(api.put(`/ifps/admin/identity-states/${id}`, body)),
  myIdentityState:   ()                  => unwrap(api.get('/ifps/citizen/identity/state')),
  myIdentityHistory: ()                  => unwrap(api.get('/ifps/citizen/identity/history')),

  // Disputes
  listDisputes:     params               => unwrap(api.get('/ifps/admin/disputes',             { params })),
  getDispute:       id                   => unwrap(api.get(`/ifps/citizen/disputes/${id}`)),
  myDisputes:       params               => unwrap(api.get('/ifps/citizen/disputes',           { params })),
  openDispute:      payload              => unwrap(api.post('/ifps/citizen/disputes', payload)),
  submitDisputeEvidence: (id, body)      => unwrap(api.post(`/ifps/citizen/disputes/${id}/evidence`, body)),
  resolveDispute:   (id, body)           => unwrap(api.put(`/ifps/admin/disputes/${id}/resolve`, body)),

  // Escalations (legacy IfpsEscalation surface — different from P4-14 queue below)
  listEscalations:    params             => unwrap(api.get('/ifps/admin/escalations',          { params })),
  getEscalation:      id                 => unwrap(api.get(`/ifps/admin/escalations/${id}`)),
  triggerEscalationDispatch: (id)        => unwrap(api.post(`/ifps/admin/escalations/${id}/dispatch`)),
  acknowledgeEscalation:(id, body)       => unwrap(api.post(`/ifps/admin/escalations/${id}/acknowledge`, body || {})),
  assignEscalation:   (id, assigneeId)   => unwrap(api.post(`/ifps/admin/escalations/${id}/assign`, { assignee_id: assigneeId })),
  resolveEscalation:  (id, body)         => unwrap(api.post(`/ifps/admin/escalations/${id}/resolve`, body)),

  // P4-14 Human Escalation & Adjudication Queue
  listQueueItems:     params             => unwrap(api.get('/ifps/admin/queue',                { params })),
  getQueueItem:       id                 => unwrap(api.get(`/ifps/admin/queue/${id}`)),
  enqueueItem:        body               => unwrap(api.post('/ifps/admin/queue', body)),
  claimQueueItem:     (id, body)         => unwrap(api.post(`/ifps/admin/queue/${id}/claim`, body || {})),
  unclaimQueueItem:   id                 => unwrap(api.post(`/ifps/admin/queue/${id}/unclaim`)),
  reassignQueueItem:  (id, body)         => unwrap(api.post(`/ifps/admin/queue/${id}/reassign`, body)),
  addQueueNote:       (id, note)         => unwrap(api.post(`/ifps/admin/queue/${id}/notes`, { note })),
  resolveQueueItem:   (id, body)         => unwrap(api.post(`/ifps/admin/queue/${id}/resolve`, body)),
  escalateQueueItem:  (id, body)         => unwrap(api.post(`/ifps/admin/queue/${id}/escalate`, body)),
  sweepExpiredQueue:  ()                 => unwrap(api.post('/ifps/admin/queue/sweep-expired')),

  // P4-15 Participant enrollment — citizen
  myEnrollments:      ()                 => unwrap(api.get('/ifps/citizen/enrollments')),
  enrollChannel:      body               => unwrap(api.post('/ifps/citizen/enrollments', body)),
  withdrawChannel:    (channel, reason)  => unwrap(api.post(`/ifps/citizen/enrollments/${channel}/withdraw`, { reason })),

  // P4-15 Participant enrollment — admin
  listEnrollments:    params             => unwrap(api.get('/ifps/admin/enrollments',          { params })),
  revokeEnrollment:   (userId, channel, reason) =>
                                            unwrap(api.post(`/ifps/admin/enrollments/${userId}/${channel}/revoke`, { reason })),

  // P4-07/08/09/16 Institutional onboarding
  institutionSnapshot:    code           => unwrap(api.get(`/ifps/admin/institutions/${code}/snapshot`)),

  listBankAddendum:       params         => unwrap(api.get('/ifps/admin/bank-addendum',         { params })),
  acceptBankAddendum:     body           => unwrap(api.post('/ifps/admin/bank-addendum', body)),
  withdrawBankAddendum:   (code, reason) => unwrap(api.post(`/ifps/admin/bank-addendum/${code}/withdraw`, { reason })),

  listTiers:              params         => unwrap(api.get('/ifps/admin/tiers',                 { params })),
  electTier:              body           => unwrap(api.post('/ifps/admin/tiers', body)),
  revokeTier:             (code, tier, reason) =>
                                            unwrap(api.post(`/ifps/admin/tiers/${code}/${tier}/revoke`, { reason })),

  listSafeEntity:         params         => unwrap(api.get('/ifps/admin/safe-entity',           { params })),
  attestSafeEntity:       body           => unwrap(api.post('/ifps/admin/safe-entity', body)),
  withdrawSafeEntity:     (code, body)   => unwrap(api.post(`/ifps/admin/safe-entity/${code}/withdraw`, body)),

  listPartnerPolicy:      params         => unwrap(api.get('/ifps/admin/partner-policy',        { params })),
  bindPartnerPolicy:      body           => unwrap(api.post('/ifps/admin/partner-policy', body)),
  revokePartnerPolicy:    (code, reason) => unwrap(api.post(`/ifps/admin/partner-policy/${code}/revoke`, { reason })),
};

// ── FIRL ─────────────────────────────────────────────────────────────────────
//
// Routes match the canonical BE under /api/v1/firl/* per routes/firl.php.
export const firlApi = {
  // FFOs (Funds Flow Objects) — canonical 5-category primitive
  listFfos:         params      => unwrap(api.get('/firl/ffo',                   { params })),
  createFfo:        payload     => unwrap(api.post('/firl/ffo', payload)),
  getFfo:           id          => unwrap(api.get(`/firl/ffo/${id}`)),
  updateFfo:        (id, body)  => unwrap(api.put(`/firl/ffo/${id}`, body)),
  ffoLineage:       id          => unwrap(api.get(`/firl/ffo/${id}/lineage`)),
  ffoAuditTrail:    id          => unwrap(api.get(`/firl/ffo/${id}/audit`)),
  createChildFfo:   (id, body)  => unwrap(api.post(`/firl/ffo/${id}/children`, body)),

  // Authority chain (5-stage lineage validation per FFO)
  validateAuthority: payload    => unwrap(api.post('/firl/authority/validate', payload)),
  getAuthorityChain: ffoId      => unwrap(api.get(`/firl/authority/${ffoId}/chain`)),
  bindAuthority:     (id, body) => unwrap(api.post(`/firl/authority/${id}/chain`, body)),

  // Recipients (Recipient Identity Objects — RIO)
  listRecipients:    params     => unwrap(api.get('/firl/recipients',                { params })),
  createRecipient:   payload    => unwrap(api.post('/firl/recipients', payload)),
  getRecipient:      id         => unwrap(api.get(`/firl/recipients/${id}`)),
  verifyRecipient:   (id, body) => unwrap(api.post(`/firl/recipients/${id}/verify`, body)),
  recipientCluster:  id         => unwrap(api.get(`/firl/recipients/${id}/cluster`)),

  // Pre-Disbursement Integrity Gate
  evaluateGate:      ffoId      => unwrap(api.post(`/firl/gate/${ffoId}/evaluate`)),
  getGateDecision:   ffoId      => unwrap(api.get(`/firl/gate/${ffoId}/decision`)),
  stepUpVerify:      (id, body) => unwrap(api.post(`/firl/gate/${id}/step-up`, body)),
  humanOverride:     (id, body) => unwrap(api.post(`/firl/gate/${id}/override`, body)),

  // Monitoring
  listAnomalies:     params     => unwrap(api.get('/firl/monitoring/anomalies',      { params })),
  ffoAnomalies:      ffoId      => unwrap(api.get(`/firl/monitoring/anomalies/${ffoId}`)),
  updateAnomalySeverity: (id, body) => unwrap(api.put(`/firl/monitoring/anomalies/${id}/severity`, body)),

  /**
   * Record an analyst's verdict on an anomaly.
   *
   * There is no dedicated review endpoint — the severity endpoint is the one
   * mutation the backend exposes, and it accepts an optional `status`
   * alongside a mandatory `severity` + `rationale`. So a review IS a
   * status change with a rationale, and the caller must pass the anomaly's
   * CURRENT severity through unchanged; omitting it would 422, and inventing
   * one would silently re-grade the finding.
   *
   * @param {{severity: string, status: string, rationale: string}} body
   */
  reviewAnomaly: (id, body) => unwrap(api.put(`/firl/monitoring/anomalies/${id}/severity`, body)),

  // Enforcement & recovery
  listEnforcements:  params     => unwrap(api.get('/firl/enforcement',               { params })),
  suspendFfo:        (id, body) => unwrap(api.post(`/firl/enforcement/${id}/suspend`, body)),
  terminateFfo:      (id, body) => unwrap(api.post(`/firl/enforcement/${id}/terminate`, body)),
  initiateRecovery:  (id, body) => unwrap(api.post(`/firl/enforcement/${id}/recovery`, body)),
  referFfo:          (id, body) => unwrap(api.post(`/firl/enforcement/${id}/refer`, body)),
};

// ── VERIS ────────────────────────────────────────────────────────────────────
export const verisApi = {
  // ── Registration Intake Gateway ────────────────────────────────────────────
  intakeRegistration: payload      => unwrap(api.post('/veris/registrations', payload)),
  listRegistrations:  params       => unwrap(api.get('/veris/registrations',           { params })),
  getRegistration:    id           => unwrap(api.get(`/veris/registrations/${id}`)),
  updateRegistration: (id, body)   => unwrap(api.put(`/veris/registrations/${id}`, body)),
  registrationAudit:  id           => unwrap(api.get(`/veris/registrations/${id}/audit`)),

  // ── Cross-Jurisdiction Deduplication Service ───────────────────────────────
  // Canonical prefix is /veris/deduplication (NOT /veris/dedup).
  runDedup:           ()           => unwrap(api.post('/veris/deduplication/run')),
  listDedupMatches:   params       => unwrap(api.get('/veris/deduplication/matches',   { params })),
  getDedupMatch:      id           => unwrap(api.get(`/veris/deduplication/matches/${id}`)),
  resolveMatch:       (id, body)   => unwrap(api.post(`/veris/deduplication/matches/${id}/resolve`, body)),

  // ── Eligibility State Manager ──────────────────────────────────────────────
  // Eligibility is registration-scoped but lives under /veris/eligibility/{id}.
  eligibilityState:   id           => unwrap(api.get(`/veris/eligibility/${id}/state`)),
  eligibilityHistory: id           => unwrap(api.get(`/veris/eligibility/${id}/history`)),
  transitionEligibility: (id, body) => unwrap(api.post(`/veris/eligibility/${id}/transition`, body)),
  restoreEligibility:    (id, body) => unwrap(api.post(`/veris/eligibility/${id}/restore`, body)),
  // Back-compat alias for callers written against the old guessed path.
  setEligibility:     (id, body)   => unwrap(api.post(`/veris/eligibility/${id}/transition`, body)),

  // ── Dual-Control (Non-Bypassability Invariant, App 4 §12.3) ────────────────
  listDualControl:      params     => unwrap(api.get('/veris/dual-control',            { params })),
  getDualControl:       id         => unwrap(api.get(`/veris/dual-control/${id}`)),
  submitDualControl:    body       => unwrap(api.post('/veris/dual-control', body)),
  firstApproveDual:     id         => unwrap(api.post(`/veris/dual-control/${id}/approve-first`)),
  secondApproveDual:    id         => unwrap(api.post(`/veris/dual-control/${id}/approve-second`)),
  executeDualControl:   id         => unwrap(api.post(`/veris/dual-control/${id}/execute`)),
  rejectDualControl:    (id, reason) => unwrap(api.post(`/veris/dual-control/${id}/reject`, { reason })),

  // ── Change Propagation & Notification Bus ──────────────────────────────────
  listPropagationEvents: params    => unwrap(api.get('/veris/propagation/events',      { params })),
  getPropagationEvent:   id        => unwrap(api.get(`/veris/propagation/events/${id}`)),
  retryPropagation:      (id, reason) => unwrap(api.post(`/veris/propagation/events/${id}/retry`, { reason })),
  propagationSubscriptions: ()     => unwrap(api.get('/veris/propagation/subscriptions')),

  // ── Disputes ───────────────────────────────────────────────────────────────
  listVerisDisputes:  params       => unwrap(api.get('/veris/disputes',                { params })),
  openVerisDispute:   body         => unwrap(api.post('/veris/disputes', body)),
  getVerisDispute:    id           => unwrap(api.get(`/veris/disputes/${id}`)),
  submitDisputeEvidence: (id, body) => unwrap(api.post(`/veris/disputes/${id}/evidence`, body)),
  resolveVerisDispute:   (id, body) => unwrap(api.post(`/veris/disputes/${id}/resolve`, body)),
};

// ── Hub ──────────────────────────────────────────────────────────────────────
export const hubApi = {
  dashboard: ()       => unwrap(api.get('/hub/dashboard')),
  audit:     params   => unwrap(api.get('/hub/audit',           { params })),
  auditOne:  id       => unwrap(api.get(`/hub/audit/${id}`)),
  exportOne: id       => unwrap(api.get(`/hub/audit/${id}/export`)),
  routing:   params   => unwrap(api.get('/hub/routing/log',     { params })),
  routingStats: ()    => unwrap(api.get('/hub/routing/stats')),
};

// ── National-Security Alerts ────────────────────────────────────────────────
export const nsaApi = {
  list:       params  => unwrap(api.get('/national-security/alerts',          { params })),
  applicable: params  => unwrap(api.get('/national-security/alerts/applicable', { params })),
  show:       id      => unwrap(api.get(`/national-security/alerts/${id}`)),
  intake:     payload => unwrap(api.post('/national-security/alerts', payload)),
  revoke:     (id, reason) => unwrap(api.post(`/national-security/alerts/${id}/revoke`, { reason })),
};

// ── PMICS (Post-Mortem Identity Containment Service) ────────────────────────
export const pmicsApi = {
  // Death signals
  listSignals:    params  => unwrap(api.get('/pmics/signals',                    { params })),
  showSignal:     id      => unwrap(api.get(`/pmics/signals/${id}`)),
  intakeSignal:   payload => unwrap(api.post('/pmics/signals', payload)),
  activate:       (signalId, body) => unwrap(api.post(`/pmics/signals/${signalId}/activate`, body || {})),

  // Containment actions
  listContainment:    params         => unwrap(api.get('/pmics/containment',     { params })),
  showContainment:    id             => unwrap(api.get(`/pmics/containment/${id}`)),
  reverseContainment: (id, reason)   => unwrap(api.post(`/pmics/containment/${id}/reverse`, { reason })),
};

// ── CSDR (Cross-Sector Dependency Registry) — admin ───────────────────────
export const csdrApi = {
  // Identity refs
  listIdentityRefs:    params         => unwrap(api.get('/csdr/identity-refs',          { params })),
  showIdentityRef:     id             => unwrap(api.get(`/csdr/identity-refs/${id}`)),
  createIdentityRef:   payload        => unwrap(api.post('/csdr/identity-refs', payload)),
  updateIdentityRef:   (id, body)     => unwrap(api.put(`/csdr/identity-refs/${id}`, body)),
  // Dependencies
  listDependencies:    params         => unwrap(api.get('/csdr/dependencies',           { params })),
  showDependency:      id             => unwrap(api.get(`/csdr/dependencies/${id}`)),
  createDependency:    payload        => unwrap(api.post('/csdr/dependencies', payload)),
  deprecateDependency: (id, reason)   => unwrap(api.post(`/csdr/dependencies/${id}/deprecate`, { reason })),
  // Impact assessment — by identity-ref id
  impact:              (identityRefId, params) => unwrap(api.get(`/csdr/impact/${identityRefId}`, { params })),
};

// ── CDMS (Credential Dependency Mapping Service) — admin ─────────────────
export const cdmsApi = {
  // Credential refs (BE path is `/cdms/credentials`, not `/credential-refs`)
  listCredentialRefs:   params         => unwrap(api.get('/cdms/credentials',                { params })),
  showCredentialRef:    id             => unwrap(api.get(`/cdms/credentials/${id}`)),
  createCredentialRef:  payload        => unwrap(api.post('/cdms/credentials', payload)),
  deprecateCredentialRef:(id, reason)  => unwrap(api.post(`/cdms/credentials/${id}/deprecate`, { reason })),
  // Dependencies
  listDependencies:    params          => unwrap(api.get('/cdms/dependencies',               { params })),
  showDependency:      id              => unwrap(api.get(`/cdms/dependencies/${id}`)),
  createDependency:    payload         => unwrap(api.post('/cdms/dependencies', payload)),
  deprecateDependency: (id, reason)    => unwrap(api.post(`/cdms/dependencies/${id}/deprecate`, { reason })),
  // Impact assessment — by credential-ref id
  impact:              (credentialRefId, params) => unwrap(api.get(`/cdms/impact/${credentialRefId}`, { params })),
};

// ── Judicial workflows ──────────────────────────────────────────────────────
export const judicialApi = {
  // Officer side — packets
  listPackets:          params         => unwrap(api.get('/judicial/packets',                 { params })),
  showPacket:           id             => unwrap(api.get(`/judicial/packets/${id}`)),
  createPacket:         payload        => unwrap(api.post('/judicial/packets', payload)),
  updatePacket:         (id, body)     => unwrap(api.put(`/judicial/packets/${id}`, body)),
  finalisePacket:       id             => unwrap(api.post(`/judicial/packets/${id}/finalise`)),

  // Officer side — warrant requests
  listWarrantRequests:  params         => unwrap(api.get('/judicial/requests',                { params })),
  showWarrantRequest:   id             => unwrap(api.get(`/judicial/requests/${id}`)),
  createWarrantRequest: payload        => unwrap(api.post('/judicial/requests', payload)),
  submitWarrantRequest: id             => unwrap(api.post(`/judicial/requests/${id}/submit`)),
  withdrawWarrantRequest:(id, reason)  => unwrap(api.post(`/judicial/requests/${id}/withdraw`, { reason })),

  // Reviewer side — inbox + decision
  inbox:                params         => unwrap(api.get('/judicial/inbox',                   { params })),
  showInboxItem:        id             => unwrap(api.get(`/judicial/inbox/${id}`)),
  decide:               (id, body)     => unwrap(api.post(`/judicial/inbox/${id}/decide`, body)),
};

// ── OSINT ───────────────────────────────────────────────────────────────────
export const osintApi = {
  listSources:        params         => unwrap(api.get('/osint/sources',          { params })),
  showSource:         id             => unwrap(api.get(`/osint/sources/${id}`)),
  createSource:       payload        => unwrap(api.post('/osint/sources', payload)),
  deactivateSource:   (id, reason)   => unwrap(api.post(`/osint/sources/${id}/deactivate`, { reason })),
  listSignals:        params         => unwrap(api.get('/osint/signals',          { params })),
  showSignal:         id             => unwrap(api.get(`/osint/signals/${id}`)),
  signalCorrelations: id             => unwrap(api.get(`/osint/signals/${id}/correlations`)),
  ingestSignal:       payload        => unwrap(api.post('/osint/signals', payload)),
  confirmSignal:      id             => unwrap(api.post(`/osint/signals/${id}/confirm`)),
  dismissSignal:      (id, reason)   => unwrap(api.post(`/osint/signals/${id}/dismiss`, { reason })),
  listRedFlags:       params         => unwrap(api.get('/osint/red-flags',        { params })),
  showRedFlag:        id             => unwrap(api.get(`/osint/red-flags/${id}`)),
  redFlagDashboard:   params         => unwrap(api.get('/osint/red-flags/dashboard', { params })),
  raiseRedFlag:       payload        => unwrap(api.post('/osint/red-flags', payload)),
  reviewRedFlag:      (id, body)     => unwrap(api.post(`/osint/red-flags/${id}/review`, body)),
};

// ── Vision Layer — citizen self-service (P6-05 / P6-10) ────────────────────
export const visionApi = {
  // Camera enrollment lifecycle
  myCameras:           ()             => unwrap(api.get('/vision/citizen/cameras')),
  cameraLedger:        params         => unwrap(api.get('/vision/citizen/cameras/ledger',           { params })),
  cameraShow:          code           => unwrap(api.get(`/vision/citizen/cameras/${code}`)),
  enrollCamera:        body           => unwrap(api.post('/vision/citizen/cameras', body)),
  pauseCamera:         (code, reason) => unwrap(api.post(`/vision/citizen/cameras/${code}/pause`, { reason })),
  resumeCamera:        code           => unwrap(api.post(`/vision/citizen/cameras/${code}/resume`)),
  updateCameraConsent: (code, triggerConsent) =>
                                          unwrap(api.put(`/vision/citizen/cameras/${code}/consent`, { trigger_consent: triggerConsent })),
  withdrawCamera:      (code, reason) => unwrap(api.post(`/vision/citizen/cameras/${code}/withdraw`, { reason })),

  // Triggered-clip ledger
  cameraClips:         (code, params) => unwrap(api.get(`/vision/citizen/cameras/${code}/clips`, { params })),

  // Live-stream activation
  pendingLiveStreams:  ()             => unwrap(api.get('/vision/citizen/live-stream-requests')),
  liveStreamLedger:    params         => unwrap(api.get('/vision/citizen/live-stream-requests/ledger', { params })),
  grantLiveStream:     id             => unwrap(api.post(`/vision/citizen/live-stream-requests/${id}/grant`)),
  denyLiveStream:      (id, reason)   => unwrap(api.post(`/vision/citizen/live-stream-requests/${id}/deny`, { reason })),
  revokeLiveStream:    (id, reason)   => unwrap(api.post(`/vision/citizen/live-stream-requests/${id}/revoke`, { reason })),

  // Transparency log (P6-10)
  transparency:        params         => unwrap(api.get('/vision/citizen/transparency', { params })),

  // ── Citizen — vehicle telematics (P6-07) ────────────────────────────────────
  myVehicles:          ()             => unwrap(api.get('/vision/citizen/vehicles')),
  registerVehicle:     body           => unwrap(api.post('/vision/citizen/vehicles', body)),
  updateVehicleConsent:(code, body)   => unwrap(api.put(`/vision/citizen/vehicles/${code}/consent`, body)),
  pauseVehicle:        (code, reason) => unwrap(api.post(`/vision/citizen/vehicles/${code}/pause`, { reason })),
  resumeVehicle:       code           => unwrap(api.post(`/vision/citizen/vehicles/${code}/resume`)),
  withdrawVehicle:     (code, reason) => unwrap(api.post(`/vision/citizen/vehicles/${code}/withdraw`, { reason })),
  vehiclePackets:      (code, params) => unwrap(api.get(`/vision/citizen/vehicles/${code}/packets`, { params })),

  // ── Agency — fixed-infrastructure sensors (P6-06) ───────────────────────────
  listSensorSources:      params         => unwrap(api.get('/vision/agency/sensor-sources', { params })),
  registerSensorSource:   body           => unwrap(api.post('/vision/agency/sensor-sources', body)),
  getSensorSource:        code           => unwrap(api.get(`/vision/agency/sensor-sources/${code}`)),
  pauseSensorSource:      (code, reason) => unwrap(api.post(`/vision/agency/sensor-sources/${code}/pause`, { reason })),
  resumeSensorSource:     code           => unwrap(api.post(`/vision/agency/sensor-sources/${code}/resume`)),
  deactivateSensorSource: (code, reason) => unwrap(api.post(`/vision/agency/sensor-sources/${code}/deactivate`, { reason })),
  sensorSourceClips:      (code, params) => unwrap(api.get(`/vision/agency/sensor-sources/${code}/clips`, { params })),

  // ── Agency — fleet telematics (P6-07) ───────────────────────────────────────
  listAgencyVehicles:      params         => unwrap(api.get('/vision/agency/vehicles', { params })),
  registerAgencyVehicle:   body           => unwrap(api.post('/vision/agency/vehicles', body)),
  deactivateAgencyVehicle: (code, reason) => unwrap(api.post(`/vision/agency/vehicles/${code}/deactivate`, { reason })),
  agencyVehiclePackets:    (code, params) => unwrap(api.get(`/vision/agency/vehicles/${code}/packets`, { params })),

  // ── Admin — threat-packaging bundle lifecycle (P6-04) ───────────────────────
  listThreatBundles:  params         => unwrap(api.get('/vision/admin/threat-bundles', { params })),
  getThreatBundle:    id             => unwrap(api.get(`/vision/admin/threat-bundles/${id}`)),
  // Gated by PrivacyGuardrails — manual clip viewing requires a confirmed
  // bundle reference, and the access is written to the camera owner's ledger.
  getBundleClip:      id             => unwrap(api.get(`/vision/admin/threat-bundles/${id}/clip`)),
  packageClip:        (clipId, body) => unwrap(api.post(`/vision/admin/clips/${clipId}/package`, body)),
  verifyBundle:       (id, notes)    => unwrap(api.post(`/vision/admin/threat-bundles/${id}/verify`, { notes })),
  routeBundle:        id             => unwrap(api.post(`/vision/admin/threat-bundles/${id}/route`)),
  resolveBundle:      (id, body)     => unwrap(api.post(`/vision/admin/threat-bundles/${id}/resolve`, body)),
  revokeBundle:       (id, reason)   => unwrap(api.post(`/vision/admin/threat-bundles/${id}/revoke`, { reason })),

  // ── Admin — live-stream oversight (P6-03 admin side) ────────────────────────
  listStreamRequests:  params           => unwrap(api.get('/vision/admin/live-stream-requests', { params })),
  requestStream:       (bundleId, body) => unwrap(api.post(`/vision/admin/threat-bundles/${bundleId}/live-stream-request`, body)),
  revokeStreamAsAdmin: (id, reason)     => unwrap(api.post(`/vision/admin/live-stream-requests/${id}/revoke`, { reason })),
  sweepExpiredStreams: ()               => unwrap(api.post('/vision/admin/live-stream-requests/sweep-expired')),
};

// ── CBIM admin (P5-09) ──────────────────────────────────────────────────────
export const cbimAdminApi = {
  dispatches:        params           => unwrap(api.get('/cbim/admin/dispatches',           { params })),
  dispatchStats:     params           => unwrap(api.get('/cbim/admin/dispatch-stats',       { params })),
  reputations:       params           => unwrap(api.get('/cbim/admin/reputations',          { params })),
  reputationShow:    userId           => unwrap(api.get(`/cbim/admin/reputations/${userId}`)),
  lockouts:          params           => unwrap(api.get('/cbim/admin/lockouts',             { params })),
  liftLockout:       (userId, reason) => unwrap(api.post(`/cbim/admin/lockouts/${userId}/lift`, { reason })),
  manualLockout:     body             => unwrap(api.post('/cbim/admin/lockouts', body)),
  sweepExpiredBroadcasts: ()          => unwrap(api.post('/cbim/admin/broadcasts/sweep-expired')),
};

// ── NCIC / NLETS / III gateway (Phase 10 #1) ──────────────────────────────────
//
// Routes match BE under /api/v1/ncic/* per routes/ncic.php.
// EVERY query is audit-logged BE-side with officer + purpose + masked query.
export const ncicApi = {
  types:        ()        => unwrap(api.get('/ncic/types')),
  query:        body      => unwrap(api.post('/ncic/query', body)),
  audit:        params    => unwrap(api.get('/ncic/audit', { params })),
};

// ── BWC — Body-Worn Camera (Phase 10 #4) ──────────────────────────────────────
//
// Routes match BE under /api/v1/bwc/* per routes/bwc.php.
export const bwcApi = {
  listRecordings:  params               => unwrap(api.get('/bwc/recordings', { params })),
  getRecording:    id                   => unwrap(api.get(`/bwc/recordings/${id}`)),
  uploadManual:    (formData)           => unwrap(api.post('/bwc/upload', formData, {
                                              headers: { 'Content-Type': 'multipart/form-data' },
                                            })),
  linkToReport:    (recordingId, reportId) =>
                                          unwrap(api.post(`/bwc/recordings/${recordingId}/link`, { report_id: reportId })),
};

// ── CAD — Computer-Aided Dispatch (Phase 10 #6) ───────────────────────────────
//
// Routes match BE under /api/v1/cad/* per routes/cad.php.
export const cadApi = {
  // AVL ingest (typically called from officer mobile / in-vehicle device)
  ingestPosition:  (unitId, body)           => unwrap(api.post(`/cad/units/${unitId}/avl`, body)),

  // Roster + position lookup
  units:           agencyId                 => unwrap(api.get('/cad/units',         { params: { agency_id: agencyId } })),
  nearest:         (agencyId, lat, lon, limit = 5) =>
                    unwrap(api.get('/cad/units/nearest', { params: { agency_id: agencyId, lat, lon, limit } })),

  // Assignments + state machine
  createAssignment:  body                   => unwrap(api.post('/cad/assignments', body)),
  markEnroute:       id                     => unwrap(api.put(`/cad/assignments/${id}/enroute`)),
  markOnScene:       id                     => unwrap(api.put(`/cad/assignments/${id}/on-scene`)),
  markCompleted:     id                     => unwrap(api.put(`/cad/assignments/${id}/completed`)),
  recall:            (id, body)             => unwrap(api.put(`/cad/assignments/${id}/recall`, body)),
};

// ── Platform Admin (laravel-platform-admin /api/platform/*) ───────────────────
//
// Separate axios instance because the platform-admin routes live at
// /api/platform/* (not /api/v1/*). Same auth header (Bearer Sanctum
// token) is forwarded via the interceptor below.
const platformBaseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1')
  .replace(/\/api\/v1\/?$/, '') + '/api/platform';

const platformAxios = axios.create({
  baseURL: platformBaseUrl,
  headers: { Accept: 'application/json' },
  timeout: 30000,
});

platformAxios.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const platformAdminApi = {
  getAllSettings:  ()                       => unwrap(platformAxios.get('/settings')),
  updateSettings:  (settings)               => unwrap(platformAxios.put('/settings', { settings })),
  flushCache:      ()                       => unwrap(platformAxios.post('/settings/cache/flush')),
  testEmail:       (toAddress)              => unwrap(platformAxios.post('/settings/test/email', { to: toAddress })),
  deleteSetting:   (key)                    => unwrap(platformAxios.delete(`/settings/${encodeURIComponent(key)}`)),
};

// Health probe — useful for Login page connection check.
export const healthApi = {
  ping: () => unwrap(api.get('/health')),
};
